import React, { useState, useMemo, useCallback } from 'react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import UsersTab from '../../components/admin/users/UsersTab';
import UserFormModal from '../../components/admin/users/UserFormModal';
import { useInfiniteUsers, UsersFilters, useDeleteUser, useSyncFromSlack } from '@/hooks/queries/useUsers';
import { useUser } from '@/hooks/queries/useUsers';
import { useDebounce } from '@/hooks/useDebounce';
import { User } from '@/services/apis/users';

const UsersContainer: React.FC = () => {
  const { t } = useTranslation('admin');

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | undefined>();
  const [selectedRole, setSelectedRole] = useState<string | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const filters: UsersFilters = useMemo(
    () => ({
      limit: 10,
      departmentId: selectedDepartmentId,
      role: selectedRole as any,
      search: debouncedSearch || undefined,
    }),
    [selectedDepartmentId, selectedRole, debouncedSearch],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isRefetching, error } =
    useInfiniteUsers(filters);

  const users = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page?.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const deleteUserMutation = useDeleteUser();
  const { isPending: isSyncing, mutateAsync: syncFromSlack } = useSyncFromSlack();
  const { data: selectedUserData, isLoading: isLoadingUser } = useUser({
    id: selectedUserId || '',
    shouldSkip: !selectedUserId || !formModalOpen,
  });

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedDepartmentId(undefined);
    setSelectedRole(undefined);
  }, []);

  const handleEdit = useCallback((user: User) => {
    setSelectedUserId(user.id);
    setFormModalOpen(true);
  }, []);

  const handleDelete = useCallback((user: User) => {
    setSelectedUserId(user.id);
    setDeleteModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedUserId(null);
    setFormModalOpen(true);
  }, []);

  const handleSyncFromSlack = useCallback(async () => {
    try {
      const result = await syncFromSlack();
      const syncResult = result || { usersCreated: 0, avatarsUpdated: 0 };
      addToast({
        title: t('users.syncFromSlackSuccess', {
          defaultValue: 'Slack sync completed. {{usersCreated}} users created, {{avatarsUpdated}} avatars updated.',
          usersCreated: syncResult.usersCreated,
          avatarsUpdated: syncResult.avatarsUpdated,
        }),
        color: 'success',
      });
    } catch (error) {
      addToast({
        title: t('users.syncFromSlackError', { defaultValue: 'Failed to sync users from Slack' }),
        color: 'danger',
      });
    }
  }, [syncFromSlack, t]);

  const confirmDelete = async () => {
    if (!selectedUserId) return;
    try {
      await deleteUserMutation.mutateAsync(selectedUserId);
      addToast({
        title: t('users.actions.deleteSuccess', { defaultValue: 'User deleted successfully' }),
        color: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedUserId(null);
    } catch (error) {
      addToast({
        title: t('users.actions.deleteError', { defaultValue: 'Failed to delete user' }),
        color: 'danger',
      });
    }
  };

  const hasActiveFilters = !!(selectedDepartmentId || selectedRole || searchQuery);

  const defaultValues = useMemo(() => {
    if (!selectedUserData) return undefined;
    return {
      firstName: selectedUserData.firstName || '',
      lastName: selectedUserData.lastName || '',
      email: selectedUserData.email,
      position: selectedUserData.position || '',
      positionDescription: selectedUserData.positionDescription || '',
      role: selectedUserData.role,
      departmentId: selectedUserData.departmentId || '',
      avatarFileId: selectedUserData.avatarFileId || null,
      avatarFile: selectedUserData.avatarFile || null,
    };
  }, [selectedUserData]);

  return (
    <>
      <UsersTab
        users={users}
        isLoading={isLoading || isRefetching}
        isFetchingNextPage={isFetchingNextPage}
        error={error?.message || null}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedDepartmentId={selectedDepartmentId}
        onDepartmentChange={setSelectedDepartmentId}
        selectedRole={selectedRole}
        onRoleChange={setSelectedRole}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteModalOpen={deleteModalOpen}
        selectedUser={selectedUserId ? users.find((u) => u.id === selectedUserId) || null : null}
        onDeleteConfirm={confirmDelete}
        onDeleteModalClose={() => {
          setDeleteModalOpen(false);
          setSelectedUserId(null);
        }}
        isDeleting={deleteUserMutation.isPending}
        loadMoreRef={loaderRef}
        hasNextPage={!!hasNextPage}
        onCreate={handleCreate}
        onSyncFromSlack={handleSyncFromSlack}
        isSyncing={isSyncing}
      />

      <UserFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        defaultValues={defaultValues}
        isLoadingUser={isLoadingUser}
      />
    </>
  );
};

export default UsersContainer;
